/**
 * downloadPaperForm - builds the requested blank prescription form from the
 * live wizard option catalog and hands the PDF to the browser as a download.
 *
 * Mirrors downloadWorkshopPdf: @react-pdf/renderer AND the document module
 * are dynamically import()-ed inside the call so the ~600 KB PDF stack stays
 * out of the initial bundle, then pdf(<Doc/>).toBlob() + a temporary <a>.
 *
 * The custom-insoles form applies the SAME catalog_card_visibility filter the
 * wizard's material picker uses, so the printed material tick-boxes always
 * match what the system currently accepts (e.g. a re-enabled polypropylene
 * shows up on the next download, no deploy needed). If the visibility fetch
 * fails we fall back to printing every material: a superset form is still
 * transcribable, a missing material is not.
 */
import * as React from 'react';
import { catalogCardVisibilityQueries } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';
import type { CardVisibilityRow, PaperFormSpec } from './paperFormSpec';

export type PaperFormVariant = 'custom-insoles' | 'sandals' | 'refurbishment';

const FILE_NAMES: Record<PaperFormVariant, string> = {
  'custom-insoles': 'insolech-blank-form-custom-insoles.pdf',
  sandals: 'insolech-blank-form-sandals.pdf',
  refurbishment: 'insolech-blank-form-refurbishment.pdf',
};

/** Browser asset resolver - same absolute-URL convention as the workshop
 *  PDF so images load under any host (dev, preview, prod). encodeURI copes
 *  with the space in the logo filename. */
function browserAssetUrl(publicPath: string): string {
  if (typeof window === 'undefined') return encodeURI(publicPath);
  return `${window.location.origin}${encodeURI(publicPath)}`;
}

async function fetchVisibilityRows(): Promise<ReadonlyArray<CardVisibilityRow> | null> {
  try {
    const rows = await catalogCardVisibilityQueries
      .catalogCardVisibilityListQuery(supabase)
      .queryFn();
    return rows.map((r) => ({
      card_code: r.card_code,
      web_app_enabled: r.web_app_enabled,
      is_draft: r.is_draft,
    }));
  } catch {
    // Offline / RLS hiccup: print the superset rather than fail the download.
    return null;
  }
}

export async function downloadPaperForm(variant: PaperFormVariant): Promise<void> {
  try {
    const [{ pdf }, specs, { PaperFormDocument }] = await Promise.all([
      import('@react-pdf/renderer'),
      import('./paperFormSpec'),
      import('./PaperFormDocument'),
    ]);

    let spec: PaperFormSpec;
    if (variant === 'custom-insoles') {
      spec = specs.buildInsolePaperForm(await fetchVisibilityRows());
    } else if (variant === 'sandals') {
      spec = specs.buildSandalPaperForm();
    } else {
      spec = specs.buildRefurbishmentPaperForm();
    }

    const blob = await pdf(
      <PaperFormDocument spec={spec} resolveAssetUrl={browserAssetUrl} />,
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = FILE_NAMES[variant];
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after a short delay so the browser has time to start the
    // download against the blob URL (workshop-pdf convention).
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    alert(`Paper form download failed: ${e instanceof Error ? e.message : String(e)}`);
  }
}
